import { createRouter, createWebHistory } from 'vue-router';
import HomeView from '../views/HomeView.vue';
import TestView from '../views/TestView.vue';
import DemoScanView from '../views/DemoScanView.vue';
import LoginView from '../views/auth/LoginView.vue';
import OAuthCallback from '../views/auth/OAuthCallback.vue';
import StartAuditView from '../views/audit/StartAuditView.vue';
import CageVerificationView from '../views/audit/CageVerificationView.vue';
import ScanAnimalsView from '../views/audit/ScanAnimalsView.vue';
import AuditCompletionView from '../views/audit/AuditCompletionView.vue';
import { useAuthStore } from '../stores/auth.store';
import { setAuthRedirectPath } from '../services/auth-redirect.service';
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL || '/'),
    routes: [
        {
            path: '/',
            name: 'home',
            component: HomeView,
        },
        {
            path: '/test',
            name: 'test',
            component: TestView,
        },
        {
            path: '/demo',
            name: 'demo',
            component: DemoScanView,
        },
        {
            path: '/login',
            name: 'login',
            component: LoginView,
            beforeEnter: (_to, _from, next) => {
                const authStore = useAuthStore();
                authStore.initializeAuth();
                if (authStore.isAuthenticated && authStore.isTokenValid) {
                    next({ path: '/' });
                }
                else {
                    next();
                }
            },
        },
        {
            path: '/auth/callback',
            name: 'oauth-callback',
            component: OAuthCallback,
            props: true,
        },
        {
            path: '/audit/start',
            name: 'audit-start',
            component: StartAuditView,
            meta: { requiresAuth: true },
        },
        {
            path: '/audit/cage-verification',
            name: 'audit-cage-verification',
            component: CageVerificationView,
            meta: { requiresAuth: true },
        },
        {
            path: '/audit/scan-animals',
            name: 'audit-scan-animals',
            component: ScanAnimalsView,
            meta: { requiresAuth: true },
        },
        {
            path: '/audit/completion',
            name: 'audit-completion',
            component: AuditCompletionView,
            meta: { requiresAuth: true },
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: HomeView, // Fallback component
            beforeEnter: (to, _from, next) => {
                if (to.path === '/auth/callback' || to.path.startsWith('/auth/callback')) {
                    next({ name: 'oauth-callback', query: to.query });
                }
                else {
                    next({ path: '/' });
                }
            },
        },
    ],
});
router.beforeEach((to, _from, next) => {
    const authStore = useAuthStore();
    // Don't initialize auth on callback route - tokens are being set there
    // But if we're reloading the callback page without a code, redirect to home
    if (to.path === '/auth/callback') {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const errorParam = urlParams.get('error');
        console.log('[Router] Callback route check:', {
            path: to.path,
            hasCode: !!code,
            hasError: !!errorParam,
            error: errorParam,
            search: window.location.search,
        });
        if (!code) {
            // No code in URL - check for error first
            if (errorParam) {
                console.error('[Router] Cognito error in callback:', {
                    error: errorParam,
                    description: urlParams.get('error_description'),
                });
                // Let the callback component handle the error
                next();
                return;
            }
            // No code and no error - probably a reload, check if we're already authenticated
            console.log('[Router] No code in callback - checking auth state');
            authStore.initializeAuth();
            if (authStore.isAuthenticated && authStore.isTokenValid) {
                console.log('[Router] Already authenticated - redirecting to audit start');
                next({ path: '/audit/start' });
                return;
            }
            // Not authenticated, redirect to login
            console.log('[Router] Not authenticated - redirecting to login');
            next({ path: '/login' });
            return;
        }
        // Has code - let the callback component handle it
        if (to.matched.length === 0) {
            try {
                const resolved = router.resolve({ name: 'oauth-callback', query: to.query });
                if (resolved.matched.length > 0) {
                    next(resolved);
                    return;
                }
            }
            catch (err) {
                console.error('Failed to resolve route:', err);
            }
            next({ name: 'oauth-callback', query: to.query });
            return;
        }
        next();
        return;
    }
    // For all other routes, initialize auth
    authStore.initializeAuth();
    if (to.matched.some((route) => route.meta.requiresAuth) &&
        (!authStore.isAuthenticated || !authStore.isTokenValid)) {
        authStore.clearAuth();
        setAuthRedirectPath(to.fullPath);
        next({
            path: '/login',
            query: {
                redirect: to.fullPath,
            },
        });
        return;
    }
    // Redirect authenticated users away from login to audit start
    if (to.path === '/login' && authStore.isAuthenticated && authStore.isTokenValid) {
        next({ path: '/audit/start' });
        return;
    }
    // Redirect authenticated users from home to audit start
    if (to.path === '/' && authStore.isAuthenticated && authStore.isTokenValid) {
        next({ path: '/audit/start' });
        return;
    }
    next();
});
export default router;
//# sourceMappingURL=index.js.map